import { useEffect, useRef, useState } from 'react'
import { MessageCircle, X } from 'lucide-react'
import SupportPanel from './SupportPanel'
import { canContactSupport } from '../../hooks/useSupport'

/**
 * Floating way into support, pinned bottom-right on every screen.
 *
 * Support used to live only inside Profile, so reaching it meant abandoning
 * whatever you were doing — and a reply from the team was invisible until
 * someone thought to go looking. The launcher carries the unread count.
 */
export default function SupportWidget({ currentUser, support }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    // The list is otherwise only as fresh as the last page load.
    support.fetchTickets?.()
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open, support])

  if (!canContactSupport(currentUser)) return null

  const unread = support.unreadCount || 0

  return (
    // Clears the bottom navbar and the credit form's sticky submit on small
    // screens; sits below the z-[60] modal layer so a dialog always covers it.
    <div
      ref={ref}
      className="fixed right-4 z-[55] flex flex-col items-end gap-3 desk:right-6 desk:bottom-6"
      style={{ bottom: 'calc(92px + env(safe-area-inset-bottom))' }}
    >
      {open && (
        // A tall panel anchored to the right edge rather than a popup that grows
        // with its contents: the thread scrolls inside it and the composer stays
        // put, so the panel does not resize as the conversation grows.
        <div
          className="w-[384px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl bg-white shadow-2xl animate-fadeIn"
          style={{ height: 'min(620px, calc(100vh - 180px))' }}
        >
          <SupportPanel currentUser={currentUser} support={support} inPanel onClose={() => setOpen(false)} />
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          open ? 'Close support'
            : unread > 0 ? `Support — ${unread} unread ${unread === 1 ? 'reply' : 'replies'}`
            : 'Support'
        }
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700 active:scale-95"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[11px] font-bold leading-none text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    </div>
  )
}
